<script lang="ts" module>
  // Label / Kicker — the design-system telemetry-label primitive
  // (wiki/feature-design-system.md §Component primitives + §Typography, AC4).
  // Uppercase Be Vietnam Pro 700, wide tracking, in the muted-ink token. Two tones:
  //   • mute   — the default `--ink-mute` telemetry label
  //   • accent — tinted in the practice `--accent` (the "cue live now" kicker)
  // Optionally renders a leading accent dot/pip (the kicker's eyebrow dot).
  export type LabelTone = 'mute' | 'accent';
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    /** `mute` (default) or `accent`. */
    tone?: LabelTone;
    /** Render a leading dot in the current tone colour. */
    dot?: boolean;
    /** Letter-spacing (spec range .12–.2em; default `.16em`). */
    tracking?: string;
    class?: string;
    children: Snippet;
  }

  let {
    tone = 'mute',
    dot = false,
    tracking = '0.16em',
    class: klass = '',
    children,
  }: Props = $props();
</script>

<span
  class="vd-label vd-label--{tone} {klass}"
  style="--vd-label-track: {tracking};"
>
  {#if dot}<span class="vd-label__dot" aria-hidden="true"></span>{/if}
  {@render children()}
</span>

<style lang="scss">
  .vd-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: var(--vd-label-track);
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
  }

  .vd-label--mute {
    color: var(--ink-mute);
  }

  .vd-label--accent {
    color: var(--accent-text);
  }

  .vd-label__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    flex: none;
  }
</style>
